import * as React from "react";
import { Routes, Route, Link, Navigate } from "react-router-dom";
import Login from "./pages/login/login";
import Dashboard from "./pages/dashboard/dashboard";
import Layout from "./pages/layout/layout";
import Profile from "./pages/profile/profile";
import Directory from "./pages/directory/directory";
import PinLookup from "./PinLookup";
import ActiveUsers from "./ActiveUsers";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import RfidManagement from "./pages/rfid/rfidManagement";
import Visitors from "./pages/visitors/visitors";
import VisitorEdit from "./pages/visitors/visitorEdit";
import VisitorInfo from "./pages/visitors/visitorInfo";
import { GoogleOAuthProvider } from "@react-oauth/google";
import Staffs from "./pages/staffs/staffs";
import Datagrid from "./components/common/datagrid";
import BusManagement from "./busChaperons";
import ImageManagement from "./images";
import CheckIn from "./checkIn";
import CheckOut from "./checkOut";
import Manual from "./manualCheckIn";
import TemporaryIDManagement from "./tempID";
import TemporaryIDReports from "./tempidReports";
import ManualOut from "./manualCheckOut";
import Reports from "./reports";
import ReportsFull from "./reports-backup";
import ReportsStaff from "./reportsStaff";
import ReportsOut from "./reportsOut";
import BusAttendanceReports from "./reportsbus";
import ReportsExpanded from "./completeReports";
import StudentAttendanceDashboard from "./studentSummary";
import DownloadableReports from "./downloadableReports";
import StaffAttendanceDashboard from "./staffAttendance";
import SimpleAttendanceDashboard from "./newStudentSummary";
import StudentIdCardDefaulters from "./defaulters";
import ReportsMS from "./reportsms";
import ReportsES from "./reportses";
import ReportsHS from "./reportshs";
import ErrorLogs from "./ErrorLogs";
import DecalRequests from "./pages/decal/DecalRequests";
import SelfAttestation from "./pages/decal/SelfAttestation";
import AttestationForm from "./pages/decal/AttestationForm";
import ViewAttestation from "./pages/decal/ViewAttestation";
import MyStaffAttendance from "./myAttendanceSummary";
import VisitorReports from "./visitorReports";
import FaceConsentCapture from "./faceConsentCapture";

import StaffEdit from "./pages/staffs/staffEdit";
import EditUser from './EditUser';
import ManualCheckOut from "./manualCheckOut";
const theme = createTheme({
  palette: {
    primary: {
      main:"#2E3192",
    }
  },
  typography: {
    fontFamily: "Ubuntu",
  },
  
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          // color:"#000",
          // boxShadow: "0px 5px 10px 0px rgba(0, 0, 0, 0.5)" 
        },
        
      },
      
    },
  },
});

// ---- Auth Context ----
const AuthContext = React.createContext(null);
export function useAuth() { return React.useContext(AuthContext); }

function normalizeUser(raw) {
  if (!raw) return null;
  let parsed = null;
  try { parsed = JSON.parse(raw); } catch (e) { return null; }

  // accept roles: ['Admin','HR'] OR role: 'Admin,HR' OR role: 'all'
  const input = Array.isArray(parsed.roles) ? parsed.roles
               : parsed.role ? String(parsed.role).split(',') : [];

  // trim, dedupe, map 'all' -> 'All'
  const roles = Array.from(new Set(
    input.map(function(r){ return String(r).trim(); }).filter(Boolean)
  )).map(function(r){ return r.toLowerCase() === 'all' ? 'All' : r; });

  return { ...parsed, roles };
}

function AuthProvider({ children }) {
  const [user, setUser] = React.useState(undefined); // undefined = loading; null = logged out

  React.useEffect(function loadFromStorage() {
    const raw = typeof window !== 'undefined' ? localStorage.getItem('userData') : null;
    const u = normalizeUser(raw);
    setUser(u); // u is object or null
  }, []);

  const signOut = React.useCallback(function() {
    localStorage.removeItem('userData');
    setUser(null);
  }, []);

  const value = React.useMemo(function() { return { user, setUser, signOut }; }, [user, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---- Route guards ----
// Secretary-role titles that should access student summary and history
const SEC_ROLES = [
  'Admin Asst - HS Principal\'s Office and IB&AP Coordinator',
  'HS Registrar & Admin Assistant to the HS Counselors',
  'Admin. Asst. to MS Principal',
  'Secretary to MS Counselor',
  'Admin Asst - ES Principal\'s Office',
  'Senior Admin. Assistant. - ES Principal'
];

// Treat these as principals
const PRINCIPAL_ROLES = [
  'Secondary Associate Principal & AP/IB Coordinator',
  'High School Principal',
  'Middle School Principal',
  'Secondary Associate Principal for Student Life',
  'Elementary School Principal',
  'ES Associate Principal'
];

function RequireAuth({ children }) {
  const { user } = useAuth();
  if (user === undefined) return null; // or a spinner/skeleton
  return user ? children : <Navigate to="/login" replace />;
}

function RequireRole({ roles, children }) {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (!user) return <Navigate to='/login' replace />;

  const email = (user.email || '').toLowerCase();
  const userRoles = new Set((user.roles || []).map(function(r){ return String(r).toLowerCase(); }));

  // superuser if email matches OR role contains 'all'
  const isSuper = email === 'securityscreening@aischennai.org' || userRoles.has('all');

  const needed = (roles || []).map(function(r){ return String(r).toLowerCase(); });
  const allowed = isSuper || needed.some(function(r){ return userRoles.has(r); });

  return allowed ? children : <Navigate to='/dashboard' replace />;
}


export default function MyApp() {
  return (
    <div>
      <GoogleOAuthProvider clientId="358122337447-oroe7v76e3da5v715vuv88kqrsj32cq3.apps.googleusercontent.com">
        <ThemeProvider theme={theme}>
          <AuthProvider>
            <Routes>
              <Route path="/" element={<Layout />}>
                {/* Public */}
                <Route index element={<Login />} />
                <Route path="login" element={<Login />} />

                {/* Auth required */}
                <Route path="dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
                <Route path="profile" element={<RequireAuth><Profile /></RequireAuth>} />
                <Route path="directory" element={<RequireAuth><Directory /></RequireAuth>} />

                {/* Role-gated examples (tweak roles to your needs) */}
                <Route path="staffs" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','HR','HR Associate Director']}><Staffs /></RequireRole>} />
                <Route path="reports" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Principal','HR','HR Associate Director','Systems Administrator / R&D Coordinator','IT Manager']}><Reports /></RequireRole>} />
                <Route path="bus" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Chaperone']}><BusManagement /></RequireRole>} />
                <Route path="images" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin']}><ImageManagement /></RequireRole>} />
                <Route path="errorlogs" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin']}><ErrorLogs /></RequireRole>} />

                {/* keep wrapping the rest similarly */}
                <Route path="preregister/:id" element={<RequireAuth><VisitorEdit preRegister /></RequireAuth>} />
                <Route path="my-attendance" element={<RequireAuth><MyStaffAttendance /></RequireAuth>} />
                <Route path="decal" element={<RequireAuth><DecalRequests/></RequireAuth>} />
                <Route path="self-attestation" element={<RequireAuth><SelfAttestation/></RequireAuth>} />
                <Route path="att-form/:id" element={<RequireAuth><AttestationForm/></RequireAuth>} />
                <Route path="view-attestation/:id" element={<RequireAuth><ViewAttestation/></RequireAuth>} />
                <Route path="qrcheckin" element={<RequireAuth><RfidManagement qrCheckin={true} /></RequireAuth>} />
                <Route path="visitors" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Security']}><Visitors /></RequireRole>} />
                <Route path="visitor/:id" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Security']}><VisitorEdit /></RequireRole>} />
                <Route path="visitor/view/:id" element={<RequireAuth><VisitorInfo /></RequireAuth>} />
                <Route path="staff/:id" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','HR']}><StaffEdit /></RequireRole>} />
                <Route path="reports-staff" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','HR']}><ReportsStaff /></RequireRole>} />
                <Route path="reportsout" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Security','HR Associate Director','Systems Administrator / R&D Coordinator','IT Manager']}><ReportsOut /></RequireRole>} />
                <Route path="tempid" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Security']}><TemporaryIDManagement /></RequireRole>} />
                <Route path="tempidreports" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Security']}><TemporaryIDReports /></RequireRole>} />
                <Route path="student-summary" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Principal','Teacher','Facilities Manager','Head of School', ...SEC_ROLES, ...PRINCIPAL_ROLES]}><StudentAttendanceDashboard /></RequireRole>} />
                <Route path="staff-summary" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','HR','Principal','Head of School','HR Associate Director']}><StaffAttendanceDashboard /></RequireRole>} />
                <Route path="defaulters" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Security']}><StudentIdCardDefaulters /></RequireRole>} />
                <Route path="new-summary" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Principal','Teacher']}><SimpleAttendanceDashboard /></RequireRole>} />
                <Route path="reports-lastyear" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin']}><ReportsFull /></RequireRole>} />
                <Route path="reports-bus" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Chaperone']}><BusAttendanceReports /></RequireRole>} />
                <Route path="historical-reports" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Principal','Teacher','Facilities Manager','Head of School',...SEC_ROLES, ...PRINCIPAL_ROLES]}><DownloadableReports /></RequireRole>} />
                <Route path="/edit-user/:id" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','HR']}><EditUser /></RequireRole>} />
                <Route path="/reportsms" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Principal']}><ReportsMS /></RequireRole>} />
                <Route path="/reportses" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Principal']}><ReportsES /></RequireRole>} />
                <Route path="/reportshs" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Principal']}><ReportsHS /></RequireRole>} />
                <Route path="manual" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Security']}><Manual /></RequireRole>} />
                <Route path="manualout" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Security']}><ManualOut /></RequireRole>} />
                <Route path="check-in" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Security']}><CheckIn /></RequireRole>} />
                <Route path="check-out" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Security']}><CheckOut /></RequireRole>} />
                <Route path="visitor-reports" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Security']}><VisitorReports /></RequireRole>} />
                <Route path="face-consent" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Security']}><FaceConsentCapture /></RequireRole>} />
                <Route path="pin-lookup" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Security']}><PinLookup /></RequireRole>} />
                <Route path="active-users" element={<RequireRole roles={['Senior Web Developer','Data and Information Systems Director','Admin','Security']}><ActiveUsers /></RequireRole>} />
                {/* Catch-all */}
                <Route path="*" element={<NoMatch />} />
              </Route>
            </Routes>
          </AuthProvider>
        </ThemeProvider>
      </GoogleOAuthProvider>
    </div>
  );
}

function NoMatch() {
  return (
    <div>
      <h2>Nothing to see here!</h2>
      <p>
        <Link to="/">Go to the home page</Link>
      </p>
    </div>
  );
}
